/**
 * Helper function to check if an ID is optimistic (a UUID)
 * 
 * @param {string|number|null|undefined} id - The ID to check
 * @returns {boolean} True if the ID is optimistic (a UUID)
 */
export function isOptimistic(id) {
  // If it's not a string or is null/undefined, it's not a UUID
  if (typeof id !== 'string' || !id) {
    return false;
  }
  
  // Check if it's a UUID (contains dashes)
  return id.includes('-');
}

/**
 * Helper function to normalize IDs for consistent cache key generation
 * Handles the common pattern where:
 * - UUIDs (strings with dashes) are kept as-is for optimistic updates
 * - Numeric strings are converted to integers for server IDs
 * - Already normalized values are returned unchanged
 * 
 * @param {string|number|null|undefined} id - The ID to normalize
 * @returns {string|number|null} The normalized ID
 */
export function normalizeId(id) {
  // Handle null/undefined
  if (!id) {
    return null;
  }
  
  // If it's already a number, return as-is
  if (typeof id === 'number') {
    return id;
  }
  
  // If it's a string
  if (typeof id === 'string') {
    // Check if it's an optimistic ID (UUID)
    if (isOptimistic(id)) {
      // Keep UUIDs as strings
      return id;
    } else {
      // Convert numeric strings to integers
      const parsed = parseInt(id, 10);
      return isNaN(parsed) ? id : parsed;
    }
  }
  
  // Fallback: return as-is
  return id;
}